import React, { type ComponentType } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { flushSync } from 'react-dom';
import { PX_PER_INCH, THERMAL_PAGE_WIDTH_CSS, THERMAL_PEEL_STRIP_IN } from './thermalPrintConstants';

const PEEL_RESERVE_PX = THERMAL_PEEL_STRIP_IN * PX_PER_INCH;
/** Subpixel / border tolerance when comparing main table bottom to peel line. */
const LAYOUT_FUZZ_PX = 4;

function orderWithChunk(order: any, chunk: any[]): { orderDetails: any } {
    return {
        orderDetails: {
            ...order,
            order_item_info: chunk,
        },
    };
}

function doesTrialPageFitDom(
    root: Root,
    host: HTMLDivElement,
    PackingSlip: ComponentType<any>,
    order: any,
    pageIndex: number,
    effectivePageCount: number,
    chunk: any[],
): boolean {
    const showOrderTotals = effectivePageCount <= 1 || pageIndex === effectivePageCount - 1;
    flushSync(() => {
        root.render(
            <div
                className={`packing-slip-peel-page${pageIndex > 0 ? ' packing-slip-peel-page--continuation' : ''}`}
                style={{ lineHeight: 1.2 }}
            >
                <PackingSlip
                    {...orderWithChunk(order, chunk)}
                    continuationPage={pageIndex > 0}
                    showOrderTotals={showOrderTotals}
                />
            </div>,
        );
    });
    const wrapper = host.querySelector('.print-wrapper');
    const main = host.querySelector('.packing-slip-peel-main');
    if (!wrapper || !main) {
        return false;
    }
    const w = wrapper.getBoundingClientRect();
    const mainBottom = main.getBoundingClientRect().bottom;
    const contentBottomLimit = w.bottom - PEEL_RESERVE_PX;
    return mainBottom <= contentBottomLimit + LAYOUT_FUZZ_PX;
}

function verifyMultiPageLayoutFitsDom(
    root: Root,
    host: HTMLDivElement,
    PackingSlip: ComponentType<any>,
    order: any,
    pageChunks: any[][],
): boolean {
    const n = pageChunks.length;
    for (let pageIndex = 0; pageIndex < n; pageIndex++) {
        if (!doesTrialPageFitDom(root, host, PackingSlip, order, pageIndex, n, pageChunks[pageIndex] ?? [])) {
            return false;
        }
    }
    return true;
}

/**
 * If the last chunk has line items, try merging it into the previous chunk and append an empty
 * chunk for a totals-only final sheet. Accept only when every trial page passes the peel-line DOM check.
 */
export function consolidateTotalsOntoOwnSheet<T>(
    pages: T[][],
    order: any,
    PackingSlip: ComponentType<any>,
): T[][] {
    if (typeof document === 'undefined') {
        return pages.map((c) => [...c]);
    }
    if (pages.length < 2) {
        return pages.map((c) => [...c]);
    }
    const last = pages.at(-1);
    if (!last?.length) {
        return pages.map((c) => [...c]);
    }
    const prev = pages.at(-2);
    if (!prev) {
        return pages.map((c) => [...c]);
    }

    const trialPages: T[][] = [...pages.slice(0, -2), [...prev, ...last], []];

    const host = document.createElement('div');
    host.setAttribute('aria-hidden', 'true');
    host.style.cssText = `position:fixed;left:-10000px;top:0;width:${THERMAL_PAGE_WIDTH_CSS};visibility:hidden;pointer-events:none;z-index:-1;line-height:1.2`;
    document.body.appendChild(host);
    const root = createRoot(host);

    try {
        if (!verifyMultiPageLayoutFitsDom(root, host, PackingSlip, order, trialPages)) {
            return pages.map((c) => [...c]);
        }
        return trialPages.map((c) => [...c]);
    } finally {
        root.unmount();
        host.remove();
    }
}

function pullRowsOntoPriorPages<T>(
    working: T[][],
    root: Root,
    host: HTMLDivElement,
    PackingSlip: ComponentType<any>,
    order: any,
): boolean {
    let changed = false;
    for (let p = 0; p < working.length - 1; p++) {
        while (working[p + 1]?.length) {
            const wouldEmptyNext = working[p + 1].length === 1;
            const effectivePageCount = wouldEmptyNext ? working.length - 1 : working.length;
            const trial = [...working[p], working[p + 1][0]];
            if (!doesTrialPageFitDom(root, host, PackingSlip, order, p, effectivePageCount, trial)) {
                break;
            }
            working[p] = trial;
            working[p + 1] = working[p + 1].slice(1);
            if (working[p + 1].length === 0) {
                working.splice(p + 1, 1);
            }
            changed = true;
        }
    }
    return changed;
}

/**
 * After height-budget pagination, try moving line items from page i+1 onto page i while a
 * real off-screen render still keeps the main table above the peel strip (no overlap).
 */
export function refineThermalPagesWithDomVerify<T>(
    pages: T[][],
    order: any,
    PackingSlip: ComponentType<any>,
): T[][] {
    if (typeof document === 'undefined') {
        return pages;
    }
    if (pages.length <= 1) {
        return pages.map((c) => [...c]);
    }

    let working = pages.map((c) => [...c]);
    const host = document.createElement('div');
    host.setAttribute('aria-hidden', 'true');
    host.style.cssText = `position:fixed;left:-10000px;top:0;width:${THERMAL_PAGE_WIDTH_CSS};visibility:hidden;pointer-events:none;z-index:-1;line-height:1.2`;
    document.body.appendChild(host);
    const root = createRoot(host);

    try {
        while (pullRowsOntoPriorPages(working, root, host, PackingSlip, order)) {
            // repeat until no row can move up without crossing peel line
        }
        working = working.filter((c) => c.length > 0);
        return working.length ? working : [[]];
    } finally {
        root.unmount();
        host.remove();
    }
}
